// This file is auto-generated by @hey-api/openapi-ts

import {
  type Options,
  getRoot,
  getSystemOverview,
  getSystemLogs,
  getControlUnitLogs,
  downloadControlUnitLogFile,
  startControlUnit,
  stopControlUnit,
} from '../sdk.gen'
import { queryOptions, type UseMutationOptions, type DefaultError } from '@tanstack/react-query'
import type {
  GetRootData,
  GetSystemOverviewData,
  GetSystemLogsData,
  GetControlUnitLogsData,
  DownloadControlUnitLogFileData,
  StartControlUnitData,
  StartControlUnitResponse,
  StopControlUnitData,
  StopControlUnitResponse,
} from '../types.gen'
import { client as _heyApiClient } from './initializer'

export type QueryKey<TOptions extends Options> = [
  Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
    _id: string
    _infinite?: boolean
    tags?: ReadonlyArray<string>
  },
]

const createQueryKey = <TOptions extends Options>(
  id: string,
  options?: TOptions,
  infinite?: boolean,
  tags?: ReadonlyArray<string>
): [QueryKey<TOptions>[0]] => {
  const params: QueryKey<TOptions>[0] = {
    _id: id,
    baseUrl: options?.baseUrl || (options?.client ?? _heyApiClient).getConfig().baseUrl,
  } as QueryKey<TOptions>[0]
  if (infinite) {
    params._infinite = infinite
  }
  if (tags) {
    params.tags = tags
  }
  if (options?.body) {
    params.body = options.body
  }
  if (options?.headers) {
    params.headers = options.headers
  }
  if (options?.path) {
    params.path = options.path
  }
  if (options?.query) {
    params.query = options.query
  }
  return [params]
}

export const getRootQueryKey = (options?: Options<GetRootData>) => createQueryKey('getRoot', options)

/**
 * Service description
 * Returns the service name and the list of available endpoints.
 */
export const getRootOptions = (options?: Options<GetRootData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getRoot({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getRootQueryKey(options),
  })
}

export const getSystemOverviewQueryKey = (options?: Options<GetSystemOverviewData>) =>
  createQueryKey('getSystemOverview', options)

/**
 * Aggregated system health overview
 * Evaluates all system predicates (services, network, hardware, storage, temperatures) and aggregates their results. Predicate results are cached per predicate; predicates whose dependencies fail are reported as skipped.
 */
export const getSystemOverviewOptions = (options?: Options<GetSystemOverviewData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getSystemOverview({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getSystemOverviewQueryKey(options),
  })
}

export const getSystemLogsQueryKey = (options?: Options<GetSystemLogsData>) => createQueryKey('getSystemLogs', options)

/**
 * Tail of the full systemd journal
 * Last 250 lines of journalctl --no-pager (all units including kernel).
 */
export const getSystemLogsOptions = (options?: Options<GetSystemLogsData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getSystemLogs({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getSystemLogsQueryKey(options),
  })
}

export const getControlUnitLogsQueryKey = (options?: Options<GetControlUnitLogsData>) =>
  createQueryKey('getControlUnitLogs', options)

/**
 * Tail of citysense-control-unit journal
 * Last 250 lines of journalctl -u citysense-control-unit --no-pager.
 */
export const getControlUnitLogsOptions = (options?: Options<GetControlUnitLogsData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getControlUnitLogs({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: getControlUnitLogsQueryKey(options),
  })
}

export const downloadControlUnitLogFileQueryKey = (options?: Options<DownloadControlUnitLogFileData>) =>
  createQueryKey('downloadControlUnitLogFile', options)

/**
 * Download the newest control unit log file
 * Streams the newest file under log.directory as an attachment. Returns an empty body when no log directory or file is found.
 */
export const downloadControlUnitLogFileOptions = (options?: Options<DownloadControlUnitLogFileData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await downloadControlUnitLogFile({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: downloadControlUnitLogFileQueryKey(options),
  })
}

export const startControlUnitQueryKey = (options?: Options<StartControlUnitData>) =>
  createQueryKey('startControlUnit', options)

/**
 * Start control unit service
 * Starts the citysense-control-unit systemd unit.
 */
export const startControlUnitOptions = (options?: Options<StartControlUnitData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await startControlUnit({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: startControlUnitQueryKey(options),
  })
}

/**
 * Start control unit service
 * Starts the citysense-control-unit systemd unit.
 */
export const startControlUnitMutation = (
  options?: Partial<Options<StartControlUnitData>>
): UseMutationOptions<StartControlUnitResponse, DefaultError, Options<StartControlUnitData>> => {
  const mutationOptions: UseMutationOptions<StartControlUnitResponse, DefaultError, Options<StartControlUnitData>> = {
    mutationFn: async localOptions => {
      const { data } = await startControlUnit({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}

export const stopControlUnitQueryKey = (options?: Options<StopControlUnitData>) =>
  createQueryKey('stopControlUnit', options)

/**
 * Stop control unit service
 * Stops the citysense-control-unit systemd unit.
 */
export const stopControlUnitOptions = (options?: Options<StopControlUnitData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await stopControlUnit({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      })
      return data
    },
    queryKey: stopControlUnitQueryKey(options),
  })
}

/**
 * Stop control unit service
 * Stops the citysense-control-unit systemd unit.
 */
export const stopControlUnitMutation = (
  options?: Partial<Options<StopControlUnitData>>
): UseMutationOptions<StopControlUnitResponse, DefaultError, Options<StopControlUnitData>> => {
  const mutationOptions: UseMutationOptions<StopControlUnitResponse, DefaultError, Options<StopControlUnitData>> = {
    mutationFn: async localOptions => {
      const { data } = await stopControlUnit({
        ...options,
        ...localOptions,
        throwOnError: true,
      })
      return data
    },
  }
  return mutationOptions
}
